import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { AutopayRunPreviewScreen } from '@finance-hub/domain/autopay/screens/autopay-run-preview-screen';
import { AutopayScreen } from '@finance-hub/domain/autopay/screens/autopay-screen';
import {
  autopayTabScreens,
  customersTabScreens,
  directTabScreens,
  invoicesTabScreens,
} from '@finance-hub/finance-hub-route-config';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createStackNavigator } from '@react-navigation/stack';
import { CustomersScreen } from '@finance-hub/domain/customers/screens/customers-screen';
import {
  DefaultTheme,
  LinkingOptions,
  NavigationContainer,
} from '@react-navigation/native';
import {
  FINANCE_HUB_ROUTES,
  FINANCE_HUB_TAB_ROUTES,
} from '@/navigators/routes';
import {
  FinanceHubAutopayStackParams,
  FinanceHubCustomersStackParams,
  FinanceHubInvoicesStackParams,
  FinanceHubTabParams,
} from '@/navigators/types';
import { FinanceHubHomeScreen } from '@finance-hub/screens/finance-hub-home-screen';
import { InvoicesScreen } from '@finance-hub/domain/invoicing/screens/invoices-screen';
import { LogoLoadingIndicator } from '@/components/logo-loading-indicator';
import { NavigationStackGuard } from '@/components/navigation-stack-guard';
import { SproutKitProvider, ToastProvider } from '@usepepper/sprout-kit-ui';
import { useAppMinimumVersion } from '@/hooks/use-app-minimum-version';
import { useNavigationGuardStatus } from '@/hooks/use-navigation-guard-status';
import { useTheme } from '@/contexts/theme-context';

const Tab = createBottomTabNavigator<FinanceHubTabParams>();
const AutopayStackNav = createStackNavigator<FinanceHubAutopayStackParams>();
const CustomersStackNav =
  createStackNavigator<FinanceHubCustomersStackParams>();
const InvoicesStackNav = createStackNavigator<FinanceHubInvoicesStackParams>();

export const FinanceHubStack: React.FC = () => {
  const theme = useTheme();
  const navigationGuardStatus = useNavigationGuardStatus();
  const { appMinimumVersion } = useAppMinimumVersion();

  // LinkingOptions<T> strips nested `screens` from PathConfigMap — a known
  // React Navigation limitation (see sales-portal-stack). The nested config is
  // valid at runtime; cast at the usage site below.
  const linking = useMemo(
    () => ({
      config: {
        screens: {
          [FINANCE_HUB_TAB_ROUTES.Autopay]: { screens: autopayTabScreens },
          [FINANCE_HUB_TAB_ROUTES.Customers]: { screens: customersTabScreens },
          [FINANCE_HUB_TAB_ROUTES.Invoices]: { screens: invoicesTabScreens },
          ...directTabScreens,
        },
      },
      enabled: true,
      prefixes: ['/'],
    }),
    [],
  );

  // Show loading while auth status is being determined
  if (!navigationGuardStatus) {
    return <LogoLoadingIndicator />;
  }

  return (
    <SproutKitProvider>
      <ToastProvider placement="top-right">
        <NavigationStackGuard
          appMinimumVersion={appMinimumVersion}
          navigationGuardStatus={navigationGuardStatus}
        >
          <NavigationContainer
            independent
            linking={linking as LinkingOptions<FinanceHubTabParams>}
            theme={{
              ...DefaultTheme,
              colors: {
                ...DefaultTheme.colors,
                background: theme.colors.white,
                primary: theme.colors.primary,
              },
            }}
          >
            <Tab.Navigator
              backBehavior="history"
              initialRouteName={FINANCE_HUB_ROUTES.Home}
              screenOptions={{ headerShown: false, lazy: true }}
              tabBar={() => null}
            >
              <Tab.Screen name={FINANCE_HUB_TAB_ROUTES.Autopay}>
                {() => (
                  <AutopayStackNav.Navigator
                    initialRouteName={FINANCE_HUB_ROUTES.Autopay}
                    screenOptions={{
                      cardStyle: { flex: 1, minHeight: 0 },
                      headerShown: false,
                    }}
                  >
                    <AutopayStackNav.Screen
                      component={AutopayScreen}
                      name={FINANCE_HUB_ROUTES.Autopay}
                      options={{ title: i18n.t('finance_hub.autopay.title') }}
                    />
                    <AutopayStackNav.Screen
                      component={AutopayRunPreviewScreen}
                      name={FINANCE_HUB_ROUTES.AutopayRunPreview}
                      options={{
                        title: i18n.t(
                          'finance_hub.autopay.run_preview.breadcrumb_title',
                        ),
                      }}
                    />
                  </AutopayStackNav.Navigator>
                )}
              </Tab.Screen>
              <Tab.Screen name={FINANCE_HUB_TAB_ROUTES.Customers}>
                {() => (
                  <CustomersStackNav.Navigator
                    initialRouteName={FINANCE_HUB_ROUTES.Customers}
                    screenOptions={{
                      cardStyle: { flex: 1, minHeight: 0 },
                      headerShown: false,
                    }}
                  >
                    <CustomersStackNav.Screen
                      component={CustomersScreen}
                      name={FINANCE_HUB_ROUTES.Customers}
                      options={{ title: i18n.t('finance_hub.customers.title') }}
                    />
                  </CustomersStackNav.Navigator>
                )}
              </Tab.Screen>
              <Tab.Screen
                component={FinanceHubHomeScreen}
                name={FINANCE_HUB_ROUTES.Home}
                options={{ title: i18n.t('finance_hub.title') }}
              />
              <Tab.Screen name={FINANCE_HUB_TAB_ROUTES.Invoices}>
                {() => (
                  <InvoicesStackNav.Navigator
                    initialRouteName={FINANCE_HUB_ROUTES.Invoices}
                    screenOptions={{
                      cardStyle: { flex: 1, minHeight: 0 },
                      headerShown: false,
                    }}
                  >
                    <InvoicesStackNav.Screen
                      component={InvoicesScreen}
                      name={FINANCE_HUB_ROUTES.Invoices}
                      options={{ title: i18n.t('finance_hub.invoices.title') }}
                    />
                  </InvoicesStackNav.Navigator>
                )}
              </Tab.Screen>
            </Tab.Navigator>
          </NavigationContainer>
        </NavigationStackGuard>
      </ToastProvider>
    </SproutKitProvider>
  );
};
